import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { api, type AccountStatus } from "../../lib/api";
import { Button } from "../../app/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../app/components/ui/card";
import { Input } from "../../app/components/ui/input";
import { Badge } from "../../app/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../app/components/ui/select";
import { useAuth } from "../../providers/AuthProvider";
import { TableLayout, type TableColumn } from "../../components/dashboard";

type AdminUserRow = Awaited<ReturnType<typeof api.admin.listUsers>>["items"][number];

export function UsersManagementPage() {
  const { token, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [rows, setRows] = useState<AdminUserRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.admin.listUsers({
          token,
          q: q.trim() || undefined,
          status: statusFilter === "all" ? undefined : (statusFilter as AccountStatus),
          page: 1,
          pageSize: 50,
        });
        if (!cancelled) {
          setRows(res.items);
          setTotal(res.total);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, q, statusFilter]);

  const onViewAs = useCallback(
    async (userId: string, businessStatus: string | null | undefined) => {
      if (businessStatus !== "verified") return;
      setBusyId(userId);
      try {
        await startImpersonation(userId);
        navigate("/", { replace: true });
      } finally {
        setBusyId(null);
      }
    },
    [navigate, startImpersonation],
  );

  const columns: TableColumn<AdminUserRow>[] = useMemo(
    () => [
      { key: "name", header: "Name", render: (user) => <span className="font-medium">{user.name}</span> },
      { key: "email", header: "Email", render: (user) => user.email },
      { key: "business", header: "Business", render: (user) => user.businessName ?? "—" },
      {
        key: "verification",
        header: "Verification",
        render: (user) =>
          user.businessStatus ? <Badge variant="outline">{user.businessStatus}</Badge> : "—",
      },
      {
        key: "account",
        header: "Account",
        render: (user) => (
          <Badge variant={user.accountStatus === "active" ? "default" : "secondary"}>{user.accountStatus}</Badge>
        ),
      },
      {
        key: "actions",
        header: "Actions",
        render: (user) => (
          <Button
            size="sm"
            variant="outline"
            className="min-h-9"
            disabled={busyId === user.id || user.businessStatus !== "verified"}
            onClick={() => void onViewAs(user.id, user.businessStatus)}
          >
            {busyId === user.id ? "Starting..." : "View as business"}
          </Button>
        ),
      },
    ],
    [busyId, onViewAs],
  );

  const mobileCardRender = useCallback(
    (user: AdminUserRow) => (
      <div className="space-y-3">
        <div className="min-w-0">
          <div className="font-medium">{user.name}</div>
          <div className="truncate text-sm text-muted-foreground">{user.email}</div>
        </div>
        <dl className="grid grid-cols-2 gap-x-2 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Business</dt>
          <dd className="text-right font-medium">{user.businessName ?? "—"}</dd>
          <dt className="text-muted-foreground">Verification</dt>
          <dd className="text-right">
            {user.businessStatus ? <Badge variant="outline">{user.businessStatus}</Badge> : "—"}
          </dd>
          <dt className="text-muted-foreground">Account</dt>
          <dd className="text-right">
            <Badge variant={user.accountStatus === "active" ? "default" : "secondary"}>{user.accountStatus}</Badge>
          </dd>
        </dl>
        <Button
          size="sm"
          variant="outline"
          className="w-full min-h-10"
          disabled={busyId === user.id || user.businessStatus !== "verified"}
          onClick={() => void onViewAs(user.id, user.businessStatus)}
        >
          {busyId === user.id ? "Starting..." : "View as business"}
        </Button>
      </div>
    ),
    [busyId, onViewAs],
  );

  return (
    <div className="space-y-6 p-4 md:p-6">
      <div>
        <h1 className="mb-1 text-2xl font-semibold text-foreground md:text-3xl">Users Management</h1>
        <p className="text-muted-foreground">Manage business owner accounts (mock).</p>
      </div>

      <Card className="border-0 shadow-sm">
        <CardHeader>
          <CardTitle>Business owners</CardTitle>
          <CardDescription>{total} total · search and filter (mock pagination API shape)</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
            <Input
              placeholder="Search name, email, or business..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="min-h-9 w-full min-w-0 sm:max-w-md"
            />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="min-h-9 w-full min-w-0 sm:w-[180px]">
                <SelectValue placeholder="Account status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
              </SelectContent>
            </Select>
            <Button
              type="button"
              variant="outline"
              className="min-h-9 w-full sm:w-auto"
              onClick={() => {
                setQ("");
                setStatusFilter("all");
              }}
            >
              Reset
            </Button>
          </div>

          {loading ? (
            <div className="text-sm text-muted-foreground">Loading...</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-muted-foreground">No users match.</div>
          ) : (
            <div className="min-w-0 rounded-md border md:border-0">
              <TableLayout columns={columns} rows={rows} mobileCardRender={mobileCardRender} />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
